import React, { useState, useEffect } from "react";
import Layout from "./Layout"; // 공통 네비게이션 레이아웃
import "../css/UserList.css"; // 스타일링 파일
import "material-icons/iconfont/material-icons.css";
import { useNavigate } from "react-router-dom";
import { defaultAxiosInstance } from "../api/defaultInstance";
import PriceTypeBadge from "../modules/subscription/PriceTypeBadge";
import { PRICE_TYPES } from "../modules/subscription/priceType";

function UserList() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]); // 초기값을 빈 배열로 설정
  const [totalUserCount, setTotalUserCount] = useState(0); // 전체 사용자 수
  const [search, setSearch] = useState(''); // 입력 중인 검색어
  const [appliedSearch, setAppliedSearch] = useState(''); // 실제 적용된 검색어
  const [loading, setLoading] = useState(true); // 로딩 상태
  const [error, setError] = useState(null); // 에러 상태
  const [activeMembershipFilter, setActiveMembershipFilter] = useState(null);
  const [activeUserTypeFilter, setActiveUserTypeFilter] = useState(null);
  const [activePriceTypeFilter, setActivePriceTypeFilter] = useState(null); // 결제 유형(정상가·50% 할인·100원 혜택)
  const [page, setPage] = useState(1); // 현재 페이지

  const limit = 100; // 페이지당 사용자 수

  // 서버에서 사용자 정보 가져오기
  const fetchUsers = async (subscriptionType = null, userType = null, pageNum = 1, searchText = null, priceType = null) => {
    try {
      setLoading(true); // 로딩 시작
      
      const params = { page: pageNum, limit };
      
      if (subscriptionType) params.subscriptionType = subscriptionType;
      if (userType) params.userType = userType;
      if (priceType) params.priceType = priceType;
      if (searchText && searchText.trim()) params.searchText = searchText.trim();

      const response = await defaultAxiosInstance.get(`/admin/users`, { params });
    
      // API 응답 구조: { users: [...], totalUserCount: number }
      // users가 배열인지 확인하고, 배열이 아니면 빈 배열로 설정
      const usersData = response.data?.users;
      const safeUsers = Array.isArray(usersData) ? usersData : [];
      const totalCount = Number(response.data?.totalUserCount) || 0;
      
      setUsers(safeUsers); // 새로운 목록으로 교체
      setTotalUserCount(totalCount); // 전체 사용자 수 업데이트
      
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err); // 에러 상태 업데이트
      setUsers([]); // 에러 발생 시 빈 배열로 설정
      setTotalUserCount(0); // 에러 발생 시 0으로 설정
    } finally {
      setLoading(false); // 로딩 종료
    }
  };

  // 필터나 검색어 변경 시 페이지를 1로 리셋
  useEffect(() => {
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMembershipFilter, activeUserTypeFilter, activePriceTypeFilter, appliedSearch]);

  // 데이터 로드: 필터/검색/페이지 변경 시
  useEffect(() => {
    fetchUsers(activeMembershipFilter, activeUserTypeFilter, page, appliedSearch, activePriceTypeFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMembershipFilter, activeUserTypeFilter, activePriceTypeFilter, appliedSearch, page]);

  const handleMembershipFilterClick = (type) => {
    const newFilter = type === 'all' ? undefined : (activeMembershipFilter === type ? undefined : type);
    setActiveMembershipFilter(newFilter);
    setPage(1);
  };

  const handleUserTypeFilterClick = (type) => {
    const newFilter = type === 'all' ? undefined : (activeUserTypeFilter === type ? undefined : type);
    setActiveUserTypeFilter(newFilter);
    setPage(1);
  };

  const handlePriceTypeFilterClick = (type) => {
    const newFilter = type === 'all' ? undefined : (activePriceTypeFilter === type ? undefined : type);
    setActivePriceTypeFilter(newFilter);
    setPage(1);
  };

  // 검색 실행 핸들러
  const handleSearch = () => {
    setAppliedSearch(search);
    setPage(1);
  };

  // 엔터 키 입력 핸들러
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // 페이지네이션 핸들러
  const handlePrevPage = () => {
    if (page > 1) {
      setPage(page - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleNextPage = () => {
    const totalPages = Math.ceil(totalUserCount / limit);
    if (page < totalPages) {
      setPage(page + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePageClick = (pageNum) => {
    setPage(pageNum);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // 페이지 번호 계산
  const getPageNumbers = () => {
    const pages = [];
    const totalPages = Math.ceil(totalUserCount / limit);
    const maxVisible = 7; // 표시할 최대 페이지 수
    
    if (totalPages <= maxVisible) {
      // 전체 페이지가 적으면 모두 표시
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // 첫 페이지
      pages.push(1);
      
      if (page <= 4) {
        // 앞부분: 1, 2, 3, 4, 5, ..., 마지막
        for (let i = 2; i <= 5; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (page >= totalPages - 3) {
        // 뒷부분: 1, ..., 마지막-4, 마지막-3, 마지막-2, 마지막-1, 마지막
        pages.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // 중간: 1, ..., 현재-1, 현재, 현재+1, ..., 마지막
        pages.push('...');
        for (let i = page - 1; i <= page + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  // 페이지네이션 정보 계산
  const startIndex = totalUserCount === 0 ? 0 : (page - 1) * limit + 1;
  const endIndex = Math.min(page * limit, totalUserCount);

  // 클라이언트 사이드 필터링 제거 - API에서 검색 처리

  const handleEditClick = (userId) => {
    // 해당 사용자의 id로 상세 페이지 이동
    navigate(`/users/detail/${userId}`);
  };

  const handleDeleteClick = async (userId) => {
    const isConfirmed = window.confirm("해당 회원의 탈퇴를 진행하시겠습니까?");
    if(isConfirmed) {
      try {
        // 회원 정보 가져오기
        await defaultAxiosInstance.delete(`/exit/user/id`, {
          params: { userId }, // 페이지와 제한 개수 전달
        });

          // users 배열에서 삭제
          setUsers((prevUsers) => {
            if (Array.isArray(prevUsers)) {
              return prevUsers.filter((user) => user.userId !== userId);
            }
            return [];
          });

  
        alert("선택한 회원이 탈퇴 됐습니다.")
      } catch (error) {
        console.error("회원 탈퇴 실패:", error);
        alert("회원탈퇴에 실패했습니다.")
      }
    }
  };


  if (loading) return <div>로딩 중...</div>;
  if (error) return <div>에러가 발생했습니다: {error.message}</div>;

  return (
    <Layout>
      <div className="user-list-container">
        {/* 타이틀 및 검색창 */}
        <div className="header d-flex justify-content-between align-items-center">
          <h1 className="title">회원 목록 ({totalUserCount.toLocaleString()}명)</h1>
            <div className="search-container">
                <input
                    type="text"
                    className="form-control search-bar"
                    placeholder="  검색어를 입력해주세요"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={handleKeyDown}
                />
                <span className="material-icons search-icon" onClick={handleSearch} style={{ cursor: 'pointer' }}>search</span> {/* Material Icons */}
            </div>
        </div>

        <div className="filter-section">
          {/* 멤버십 등급 필터 */}
          <div className="filter-group">
            <label className="filter-label">멤버십 등급</label>
            <div className="filter-button-group">
              <button
                className={`filter-button ${activeMembershipFilter === null || activeMembershipFilter === undefined ? 'active' : ''}`}
                onClick={() => handleMembershipFilterClick('all')}
              >
                전체
              </button>
              {[
                { value: 'free', label: 'free' },
                { value: 'basic', label: 'basic' },
                { value: 'standard', label: 'standard' },
                { value: 'premium', label: 'premium' },
                { value: 'owner', label: 'owner' }
              ].map(({ value, label }) => (
                <button
                  key={value}
                  className={`filter-button ${activeMembershipFilter === value ? 'active' : ''}`}
                  onClick={() => handleMembershipFilterClick(value)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* 회원 타입 필터 */}
          <div className="filter-group">
            <label className="filter-label">회원 타입</label>
            <div className="filter-button-group">
              <button
                className={`filter-button ${activeUserTypeFilter === null || activeUserTypeFilter === undefined ? 'active' : ''}`}
                onClick={() => handleUserTypeFilterClick('all')}
              >
                전체
              </button>
              {[
                { value: 'free', label: '무료회원' },
                { value: 'paid', label: '구독회원' },
                { value: 'lecturer', label: '강사' },
                { value: 'test', label: '테스트' }
              ].map(({ value, label }) => (
                <button
                  key={value}
                  className={`filter-button ${activeUserTypeFilter === value ? 'active' : ''}`}
                  onClick={() => handleUserTypeFilterClick(value)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* 결제 유형 필터 — 이번 구독 기간 결제가 정상가인지, 50% 할인·100원 혜택인지 */}
          <div className="filter-group">
            <label className="filter-label">결제 유형</label>
            <div className="filter-button-group">
              <button
                className={`filter-button ${activePriceTypeFilter === null || activePriceTypeFilter === undefined ? 'active' : ''}`}
                onClick={() => handlePriceTypeFilterClick('all')}
              >
                전체
              </button>
              {PRICE_TYPES.map(({ key, label }) => (
                <button
                  key={key}
                  className={`filter-button ${activePriceTypeFilter === key ? 'active' : ''}`}
                  onClick={() => handlePriceTypeFilterClick(key)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* 회원 목록 테이블 */}
        <table className="table table-bordered mt-3">
          <thead>
            <tr>
              <th>순번</th> {/* 순번 열 추가 */}
              <th>ID</th>
              <th>사용자명</th>
              <th>전화번호</th>
              <th>이메일주소</th>
              <th>가입일</th>
              <th>멤버십</th>
              <th>결제 유형</th>
              <th>시작일</th>
              <th>종료일</th>
              <th>사용 기간</th>
              <th>해지 요청일</th>
              <th>관리</th>
            </tr>
          </thead>
          <tbody>
            {Array.isArray(users) ? users.map((user, index) => (
              <tr key={user.userId}>
                {/* 순번 추가 (페이지 기반) */}
                <td>{startIndex + index}</td>
                <td>{user.userId}</td>
                <td>{user.name}</td>
                <td>{user.phoneNumber}</td>
                <td>{user.email}</td>
                <td>{user.regDate}</td>
                <td>
                  <span className={`badge badge-${ user.subscriptionInfo ? user.subscriptionInfo.type.toLowerCase() : "free"}`}>
                    {user.subscriptionInfo ? user.subscriptionInfo.type : 'free'}
                  </span>
                </td>
                <td>
                  <PriceTypeBadge
                    priceType={user.subscriptionInfo?.priceType}
                    paidAmount={user.subscriptionInfo?.paidAmount}
                  />
                </td>
                <td>{user.subscriptionInfo?.startDate}</td>
                <td>{user.subscriptionInfo?.endDate}</td>
                <td>{user.subscriptionDuration + "개월"}</td>
                <td>{user.subscriptionInfo?.terminationRequestedAt}</td>
                {/*<td>{"월간구독"}</td>*/}
                <td>
                    <button className="btn btn-sm btn-outline-primary me-2" onClick={() => handleEditClick(user.userId)}>
                        <span className="material-icons">edit</span> {/* 수정 아이콘 */}
                    </button>
                    <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteClick(user.userId)}>
                        <span className="material-icons">delete</span> {/* 삭제 아이콘 */}
                    </button>
                </td>
              </tr>
            )) : null}
          </tbody>
        </table>
        
        {/* 페이지네이션 */}
        {totalUserCount > 0 && (
          <div className="pagination-info mt-3 mb-3">
            <div className="mb-2">총 {totalUserCount.toLocaleString()}개 항목 중 {startIndex.toLocaleString()} - {endIndex.toLocaleString()}</div>
            <div className="pagination d-flex justify-content-center align-items-center gap-2">
              <button 
                className="btn btn-sm btn-outline-secondary" 
                onClick={handlePrevPage} 
                disabled={page === 1}
              >
                이전
              </button>
              {/* 페이지 번호 버튼들 */}
              {getPageNumbers().map((pageNum, index) => (
                pageNum === '...' ? (
                  <span key={`ellipsis-${index}`} className="pagination-ellipsis">...</span>
                ) : (
                  <button
                    key={pageNum}
                    className={`btn btn-sm ${page === pageNum ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => handlePageClick(pageNum)}
                  >
                    {pageNum}
                  </button>
                )
              ))}
              <button 
                className="btn btn-sm btn-outline-secondary" 
                onClick={handleNextPage} 
                disabled={endIndex >= totalUserCount}
              >
                다음
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default UserList;
